import { useEffect, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { getSupabaseClient } from '../services/supabaseClient';
import { getActiveDirectoryListings, isDirectoryListingsEnabledForSlug, type DirectoryListing } from '../services/supabaseService';

interface Props {
  slug: string;
  pageType: 'blog' | 'newsroom' | 'landing';
}

const LogoLink = ({ listing }: { listing: DirectoryListing }) => (
  <a
    href={listing.url}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center justify-center transition-all duration-300 transform hover:scale-110 filter hover:brightness-125 grayscale opacity-45 hover:grayscale-0 hover:opacity-100"
  >
    {listing.logo_url ? (
      <img
        src={listing.logo_url}
        alt={listing.name}
        className="h-8 sm:h-10 w-auto max-w-[160px] object-contain select-none pointer-events-none"
      />
    ) : (
      <span className="font-outfit font-black text-sm uppercase tracking-widest text-white/45 hover:text-white/80 transition-colors">
        {listing.name}
      </span>
    )}
  </a>
);

const DirectoryListingsSection = memo(({ slug, pageType }: Props) => {
  const { t } = useTranslation();
  const [listings, setListings] = useState<DirectoryListing[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const supabase = getSupabaseClient();
    Promise.all([
      isDirectoryListingsEnabledForSlug(supabase, slug, pageType),
      getActiveDirectoryListings(supabase),
    ]).then(([enabled, data]) => {
      if (enabled && data.length > 0) { setListings(data); setReady(true); }
    }).catch(() => {});
  }, [slug, pageType]);

  if (!ready || listings.length === 0) return null;

  const ticker = [...listings, ...listings, ...listings];
  const useMarquee = listings.length > 1;
  // One full lap covers the whole list once (33.33% of the tripled track), so
  // duration must scale with item count or a long list flies by unreadably.
  const marqueeDuration = Math.max(20, listings.length * 3);

  return (
    <section className="py-12 bg-[#050810]/60 border-y border-white/5 backdrop-blur-md relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-8 sm:px-24 w-full flex flex-col md:flex-row items-start md:items-center justify-between gap-8 md:gap-16 relative z-10">

        {/* Left: label + heading + copy */}
        <div className="flex flex-col gap-2 text-left shrink-0 w-full md:w-5/12 max-w-sm">
          <span className="text-[9px] font-black text-[var(--primary)] uppercase tracking-[0.4em] select-none">
            {t('directory.listed_in')}
          </span>
          <h4 className="font-outfit font-black text-white text-xl sm:text-2xl uppercase tracking-wider leading-tight select-none">
            {t('directory.title')}
          </h4>
          <p className="text-white/40 text-xs font-bold leading-relaxed select-none">
            {t('directory.desc')}
          </p>
        </div>

        {/* Right: marquee (2+ listings) or static (1 listing) */}
        {useMarquee ? (
          <div className="relative flex overflow-hidden w-full md:w-7/12 md:flex-1 group/marquee py-2 select-none">
            {/* Fade gradients */}
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#050810] to-transparent z-20 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#050810] to-transparent z-20 pointer-events-none" />

            {/* Marquee track */}
            <div className="flex gap-16 animate-dir-marquee whitespace-nowrap items-center shrink-0" style={{ animationDuration: `${marqueeDuration}s` }}>
              {ticker.map((listing, i) => (
                <LogoLink key={`${listing.id}-${i}`} listing={listing} />
              ))}
            </div>
          </div>
        ) : (
          <div className="flex w-full md:w-7/12 md:flex-1 items-center justify-start py-2">
            <LogoLink listing={listings[0]} />
          </div>
        )}

      </div>

      <style>{`
        @keyframes dirMarquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
        .animate-dir-marquee {
          display: flex;
          animation: dirMarquee 20s linear infinite;
        }
        .animate-dir-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>
    </section>
  );
});

export default DirectoryListingsSection;
